"use client";

import { useState, useMemo } from "react";
import styled from "styled-components";
import { Button } from "@/components/ui/button";
import { Calendar } from "lucide-react";
import { getMonthPeriod } from "@/utils/date";

interface PeriodSelectorProps {
  from: string;
  to: string;
  onChange: (from: string, to: string) => void;
}

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const DateDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: var(--color-text-secondary);

  svg {
    width: 16px;
    height: 16px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 4px;
`;

function getMonthDisplayLabel(offset: number): string {
  const date = new Date();
  date.setDate(1);
  date.setMonth(date.getMonth() + offset);
  const monthName = date.toLocaleDateString("ru-RU", { month: "long" });
  return monthName.charAt(0).toUpperCase() + monthName.slice(1);
}

export function PeriodSelector({ from, to, onChange }: PeriodSelectorProps) {
  const [activeMonth, setActiveMonth] = useState<number>(0);

  const monthPresets = useMemo(
    () => [
      { offset: 0, label: getMonthDisplayLabel(0) },
      { offset: -1, label: getMonthDisplayLabel(-1) },
      { offset: -2, label: getMonthDisplayLabel(-2) },
    ],
    []
  );

  const handleMonthClick = (offset: number) => {
    const period = getMonthPeriod(offset);
    setActiveMonth(offset);
    onChange(period.from, period.to);
  };

  const formatPeriodDisplay = () => {
    const fromDate = new Date(from);
    const toDate = new Date(to);

    const monthsGenitive = [
      "января",
      "февраля",
      "марта",
      "апреля",
      "мая",
      "июня",
      "июля",
      "августа",
      "сентября",
      "октября",
      "ноября",
      "декабря",
    ];

    if (
      fromDate.getMonth() === toDate.getMonth() &&
      fromDate.getFullYear() === toDate.getFullYear()
    ) {
      const month = monthsGenitive[toDate.getMonth()];
      return `${fromDate.getDate()} – ${toDate.getDate()} ${month} ${toDate.getFullYear()} г.`;
    }

    const fromMonth = monthsGenitive[fromDate.getMonth()];
    const toMonth = monthsGenitive[toDate.getMonth()];
    return `${fromDate.getDate()} ${fromMonth} – ${toDate.getDate()} ${toMonth} ${toDate.getFullYear()} г.`;
  };

  return (
    <Container>
      <DateDisplay>
        <Calendar />
        <span>{formatPeriodDisplay()}</span>
      </DateDisplay>
      <ButtonGroup>
        {monthPresets.map((preset) => (
          <Button
            key={preset.offset}
            variant={activeMonth === preset.offset ? "default" : "outline"}
            size="sm"
            onClick={() => handleMonthClick(preset.offset)}
          >
            {preset.label}
          </Button>
        ))}
      </ButtonGroup>
    </Container>
  );
}
